import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { RegionInterface } from '../interfaces/region.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { ArchivableEntityDto } from '../../common/dto/archivable-entity.dto';
import {
  IsBoolean,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { Exclude, Expose } from 'class-transformer';
import {
  REGION_COST_PER_BOARD_MIN,
  EXTERIOR_PAYOUT_PERCENTAGE_MAX,
  EXTERIOR_PAYOUT_PERCENTAGE_MIN,
  REGION_COST_PER_SQFT_MIN,
  REGION_PAYMENT_RATE_MAX,
  REGION_PAYMENT_RATE_MIN,
} from '../constants/region-validation.constants';
import { RegionTimeWindowDto } from '../../region-time-window/dtos/region-time-window.dto';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';

@Exclude()
export class RegionDto
  extends IntersectionType(CommonEntityDto, ArchivableEntityDto)
  implements RegionInterface
{
  @ApiProperty({
    title: 'Name',
    description: 'The name of the Region',
  })
  @IsString()
  @MinLength(2)
  @MaxLength(100)
  @Expose()
  name!: string;

  @ApiProperty({
    title: 'Repairs Rate',
    description: 'Repairs Rate',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_PAYMENT_RATE_MIN)
  @Max(REGION_PAYMENT_RATE_MAX)
  @Expose()
  repairsRate!: number;

  @ApiProperty({
    type: 'enum',
    enum: PhaseStructureTypeEnum,
    nullable: true,
    title: 'Resource Type',
    description: 'The resource type of this Phase',
  })
  @IsEnum(PhaseStructureTypeEnum)
  @Expose()
  phaseStructureType!: PhaseStructureTypeEnum;

  @ApiProperty({
    title: 'Garage Cost Fixed',
    description: 'Garage Cost Fixed',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  garageCostFixed!: number | null;

  @ApiProperty({
    title: 'Hanging Cost Per Board',
    description: 'Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Finishing Cost Per Board',
    description: 'Finishing Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwFinishingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'HighCeiling Per Board',
    description: 'HighCeiling Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  highCeilingPerBoard!: number;

  @ApiProperty({
    title: 'Screw Inspection Per Board',
    description: 'Screw Inspection Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  screwInspectionPerBoard!: number;

  @ApiProperty({
    title: 'Spraying Cost Per Board',
    description: 'Spraying Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwSprayingCostPerBoard!: number | null;

  @ApiPropertyOptional({
    type: () => RegionTimeWindowDto,
    title: 'Region Time Window',
    description: 'Time windows associated with this region',
  })
  @Expose()
  timeWindows?: RegionTimeWindowDto[];

  @ApiProperty({
    title: 'Special Jobs Percentage',
    description: 'Special Jobs Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  specialJobsPercentage!: number;

  @ApiProperty({
    title: 'Piecework Percentage',
    description: 'Piecework Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  pieceworkPercentage!: number;

  @ApiPropertyOptional({
    type: 'boolean',
    default: false,
    title: 'Allow Payout Amount Editing',
    description:
      'Allow Supervisor to edit the contractor payout amount for Piecework/Special Jobs repairs',
  })
  @IsBoolean()
  @IsOptional()
  @Expose()
  allowPayoutAmountEditing!: boolean;

  @ApiProperty({
    title: 'Hanging Cost Per Board',
    description: 'Drywall and Paint Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Finish Level 4 Cost Per Board',
    description: 'Drywall and Paint Finish Level 4 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpFinishCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Prime Cost Per Board',
    description: 'Drywall and Paint Prime Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpPrimeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 1 Cost Per Board',
    description: 'Drywall and Paint Bump 1 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump1CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Rough Trim Cost Per Sqft',
    description: 'Drywall and Paint Rough Trim Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim1CostPerSqft!: number | null;

  @ApiProperty({
    title: 'Interior Paint Cost Per Sqft',
    description: 'Drywall and Paint Interior Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpInteriorPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 2 Cost Per Board',
    description: 'Drywall and Paint Bump 2 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump2CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Hardware Paint Cost Per Sqft',
    description: 'Drywall and Paint Hardware Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim2CostPerSqft!: number | null;

  @ApiProperty({
    title: 'Final Paint Cost Per Sqft',
    description: 'Drywall and Paint Final Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 3 Cost Per Board',
    description: 'Drywall and Paint Bump 3 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump3CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Paint Bump 1 Cost Per Sqft',
    description: 'Drywall and Paint Bump 1 Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpCMBluetapeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Paint Bump 2 Cost Per Sqft',
    description: 'Drywall and Paint Bump 2 Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalBluetapeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Interior Options Percentage',
    description: 'Interior Options Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  interiorOptionsPercentage!: number;

  @ApiProperty({
    title: 'Exterior Percentage',
    description: 'Exterior Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorOptionsPercentage!: number;

  @ApiProperty({
    title: 'Trim Option Percentage',
    description: 'Trim Option Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  trimOptionPercentage!: number;

  @ApiPropertyOptional({
    title: 'Exterior One Percentage',
    description:
      'Share of the Exterior Siding/Paint amount paid at Exterior One. Together with Exterior Two it must total 100%, and neither may be 0% or 100%. Drywall and Paint regions only.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(EXTERIOR_PAYOUT_PERCENTAGE_MIN)
  @Max(EXTERIOR_PAYOUT_PERCENTAGE_MAX)
  @Expose()
  exteriorOnePercentage!: number | null;

  @ApiPropertyOptional({
    title: 'Exterior Two Percentage',
    description:
      'Share of the Exterior Siding/Paint amount paid at Exterior Two. Together with Exterior One it must total 100%, and neither may be 0% or 100%. Drywall and Paint regions only.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(EXTERIOR_PAYOUT_PERCENTAGE_MIN)
  @Max(EXTERIOR_PAYOUT_PERCENTAGE_MAX)
  @Expose()
  exteriorTwoPercentage!: number | null;
}
